import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProject } from '../ProjectContext';
import { extractTargets, tierOptionsAtDepth } from '@shared/targetHelpers';
import { fetchUploadTargets, uploadFiles } from '../services/api';
import { Button, Card, PillGroup, FileDrop, Loading, ErrorText, errorMessage } from '../components/ui';
import ProcoreImportCard from '../components/ProcoreImport';

const key = (v) => String(v || '').trim().toLowerCase();

// The package the user picked, matched by name against the project's
// responsibilities so its id and supplier travel with the upload.
function findPackage(responsibilities, packageName) {
  const wanted = key(packageName);
  if (!wanted) return null;
  return (Array.isArray(responsibilities) ? responsibilities : []).find((resp) =>
    [resp?.name, resp?.packageName, resp?.package, resp?.title, resp?.supplier].some((v) => key(v) === wanted)) || null;
}

// The mobile app splits this across two screens (choose location, then choose
// files) because of screen size. On a desktop both fit on one page, so the
// user can see where a file is going while picking it.
export default function UploadPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { project } = useProject();

  const [apiTargets, setApiTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [packageName, setPackageName] = useState('');
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [uploadType, setUploadType] = useState('Images');
  const [tags, setTags] = useState([]);
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');

  const targets = useMemo(() => extractTargets(project || {}, apiTargets), [project, apiTargets]);
  const tierDefinitions = targets.tierDefinitions?.length ? targets.tierDefinitions : [];
  const tierPaths = targets.tierPaths || [];

  const uploadTypes = apiTargets?.uploadTypes?.length
    ? apiTargets.uploadTypes
    : ['Images', 'Drawings', 'Documents', 'Reports', 'Specifications', 'Instructions', 'QA'];
  const documentTags = apiTargets?.documentTags || [];

  useEffect(() => {
    if (!project) return;
    setLoading(true);
    fetchUploadTargets(project)
      .then(setApiTargets)
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [project]);

  function selectTierAt(depth, value) {
    setSelectedTiers((prev) => [
      ...prev.slice(0, depth),
      { tierId: tierDefinitions[depth]?.id, tierName: tierDefinitions[depth]?.name, value },
    ]);
  }

  const selectedPackage = useMemo(
    () => findPackage(targets.responsibilities, packageName),
    [targets.responsibilities, packageName],
  );

  // Package and location are independent axes: either, both or neither is a
  // valid target, and the scope just records which were used.
  const scopeType = selectedTiers.length ? 'master' : packageName ? 'package' : 'project';
  const summary = [packageName || 'Project Home', selectedTiers.map((t) => t.value).filter(Boolean).join(' / ')]
    .filter(Boolean).join(' / ');

  function toggleTag(tag) {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  }

  async function handleUpload() {
    if (!files.length) return;
    setBusy(true);
    setError('');
    setDone('');
    try {
      await uploadFiles({
        files,
        comment,
        context: {
          project,
          scopeType,
          packageName,
          tiers: selectedTiers,
          uploadType,
          tags,
          responsibilityId: selectedPackage?.id || '',
          supplier: selectedPackage?.supplier || '',
          packageSupplier: selectedPackage?.supplier || '',
        },
      });
      setDone(`Uploaded ${files.length} file${files.length === 1 ? '' : 's'} to ${summary}.`);
      setFiles([]);
      setComment('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Loading text="Loading packages and location structure…" />;

  return (
    <>
      <Card title="Will save to" subtitle={summary || 'Project Home'} />
      <ErrorText>{error}</ErrorText>
      {done && (
        <div className="card">
          <p className="saved" style={{ margin: 0 }}>{done}</p>
          <Button secondary compact onClick={() => navigate(`/projects/${encodeURIComponent(projectId)}`)} style={{ marginTop: 10 }}>View documents</Button>
        </div>
      )}

      <Card title="Upload type">
        <PillGroup items={uploadTypes} value={uploadType} onChange={setUploadType} />
      </Card>

      <Card title="Package">
        <PillGroup
          items={['Project Home', ...(targets.packages || [])]}
          value={packageName || 'Project Home'}
          onChange={(value) => setPackageName(value === 'Project Home' ? '' : value)}
          empty="No packages configured — saving to Project Home."
        />
      </Card>

      {/* One card per tier, each filtered by the tiers already chosen above
          it. A tier only appears once its parent has a value. */}
      {tierDefinitions.map((tier, depth) => {
        if (depth > selectedTiers.length) return null;
        const items = tierOptionsAtDepth(tierPaths, depth, selectedTiers.slice(0, depth));
        return (
          <Card key={tier.id} title={`${depth + 1}. ${tier.name}`}>
            <PillGroup
              items={items}
              value={selectedTiers[depth]?.value || ''}
              onChange={(value) => selectTierAt(depth, value)}
              empty={`No ${tier.name.toLowerCase()} values are configured yet.`}
            />
          </Card>
        );
      })}

      {documentTags.length > 0 && (
        <Card title="Tags" hint="At least one tag is required.">
          <div className="pill-wrap">
            {documentTags.map((tag) => (
              <button key={tag} type="button" className={`pill${tags.includes(tag) ? ' active' : ''}`} onClick={() => toggleTag(tag)}>
                {tag}
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card title="Files">
        <FileDrop files={files} onChange={setFiles} disabled={busy} />
        <div className="field" style={{ marginTop: 12 }}>
          <label className="field-label">Comment (optional)</label>
          <textarea className="input" value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        <Button onClick={handleUpload} disabled={!files.length || busy || !tags.length}>
          {busy ? 'Uploading…' : `Upload ${files.length || ''} file${files.length === 1 ? '' : 's'}`.trim()}
        </Button>
        {!tags.length && documentTags.length > 0 && <p className="card-hint" style={{ marginTop: 8 }}>Select at least one tag to upload.</p>}
      </Card>

      {/* Renders nothing unless this project came from Procore and the
          connection is live, so it costs an ordinary project no space and no
          request.

          It is handed the SAME classification chosen in the cards above, so a
          document imported from Procore is filed exactly as one dropped into
          the box would be. Imported files used to be stamped "Procore" as
          their type, supplier and package and given no location at all, which
          left them out of every filter and report the app's own documents
          appear in - for no better reason than where they came from. Repeating
          these selectors inside the card would have been the obvious fix and
          the wrong one: there is one set of choices on this page, and the two
          ways of getting a file in should both honour it. */}
      <ProcoreImportCard
        project={project}
        uploadType={uploadType}
        packageName={packageName}
        supplier={selectedPackage?.supplier || ''}
        tiers={selectedTiers}
        tags={tags}
        summary={summary}
      />
    </>
  );
}
